import { useState, useEffect, useMemo } from "react";
import { colors, css, fonts, frames, glyphs, mediaQueriesNumbers, useMediaQuery } from "../../../../../Styles";
import { AicoConnection, CheckBox, NewSelect, PageHeader, LoadingSpinner, ReactiveTable, NewButton, AreaLayout, WaitAnimation, CheckStyles, TableLayout, TableTypes } from "../../../../../Components";
import { ButtonTypes, ListData_ValueTypes, NewFieldStyles, TableStyles_Types } from "../../../../../Definitions";
import { useLazyQuery, useMutation } from "@apollo/client";
import { AVAILABLE_FIRMWARE_DEVICE, AvailableFirmwareDeviceArgs, AvailableFirmwareDeviceResponse, FIRMWARE_UPDATE_CAN, FirmwareUpdateCANArgs, FirmwareUpdateCANResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/firmwareCalls";
import { DeviceRegister, DeviceRegisterArgs, DeviceRegistersResponse, GET_DEVICE_REGISTERS } from "../../../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getDeviceRegistersCall";
import { GenericRow, useColumnConstructor } from "../../../../../Components/Tables/_Tables/useColumnConstructor";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { useAicoConnection } from "../../../../../Components/ToolsComponents/_ToolsComponents/aicoConnectionContext";
import { useTranslation } from "react-i18next";

interface DeviceResult extends DeviceRegister, GenericRow {
	shouldUpdate: boolean
	success: boolean | undefined
	id: number
}

const columnsData = [
	{ headerValue: "Device Register ID", accessor: "id", type: ListData_ValueTypes.Number, sort: true, staticColumn: true },
	{ headerValue: "CPU ID", accessor: "cpu_id", type: ListData_ValueTypes.String, sort: true, staticColumn: true },
	{ headerValue: "LC ID", accessor: "fk_lan_controllers", type: ListData_ValueTypes.Number, sort: true, staticColumn: true },
	{ headerValue: "should update", accessor: "shouldUpdateJSX", type: ListData_ValueTypes.Div, sort: false },
	{ headerValue: "result", accessor: "resultJSX", type: ListData_ValueTypes.Div, sort: false, staticColumn: true },
]

export default function FirmwareIO() {
	const { t } = useTranslation();
	const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`); 
	
	const [firmwareFileName, setFirmwareFileName] = useState<string | null>(null);
	const [devices, setDevices] = useState<DeviceResult[]>([]);
	const [isDirect, setIsDirect] = useState<boolean>(false);
	const [offset, setOffset] = useState<number>(0);
	const [hasMore, setHasMore] = useState<boolean>(false);
	const limit = 500;

	const { state: connectionState } = useAicoConnection();

	const [fetchAvailable, { data: dataAvailable, loading: loadingAvailable, error: errorAvailable }] = useLazyQuery<AvailableFirmwareDeviceResponse, AvailableFirmwareDeviceArgs>(
		AVAILABLE_FIRMWARE_DEVICE,
		{ fetchPolicy: "network-only" }
	);

	const [updateFirmware, { data: dataUpdate, loading: loadingUpdate, error: errorUpdate }] = useMutation<FirmwareUpdateCANResponse, FirmwareUpdateCANArgs>(
		FIRMWARE_UPDATE_CAN,
		{ fetchPolicy: "network-only" }
	);

	const [fetchDevices, { data: dataDevices, loading: loadingDevices, error: errorDevices }] = useLazyQuery<DeviceRegistersResponse, DeviceRegisterArgs>(
		GET_DEVICE_REGISTERS,
		{ fetchPolicy: "network-only" }
	);

	useEffect(() => {
		if (connectionState.domain) {
			resetState();
			fetchDevices({ variables: { domain: connectionState.domain, limit: limit, offset: 0 } });
			fetchAvailable({ variables: { domain: connectionState.domain } });
		} else {
			resetState();
		}
	}, [connectionState.domain, fetchDevices]);

	useEffect(() => {
		if (connectionState.domain && dataDevices?.device.registers) {
			const newDevices = dataDevices.device.registers.map((device) => {
				return {
					...device,
					shouldUpdate: false,
					success: undefined,
					id: device.device_register_id
				}
			})
			setDevices(prev => [...prev, ...newDevices]);
			setOffset((prev) => prev += newDevices.length);
			setHasMore(newDevices.length === limit);
		}
	}, [dataDevices, connectionState.domain]);

	useEffect(() => {
		const updateResults = dataUpdate?.device.firmwareUpdateCAN;
		if (updateResults) {
			setDevices((prev) => {
				return prev.map(device => {
					const updateResult = updateResults.find(e => e.id === device.device_register_id);

					const newDevice = { ...device, success: device.shouldUpdate ? updateResult?.success || false : undefined };
					return newDevice;
				})
			})
		}
	}, [dataUpdate]);

	const allSelected = devices.every(d => d.shouldUpdate);

	const selectFirmwareFile = firmwareFileName ? { label: firmwareFileName, value: firmwareFileName, id: firmwareFileName } : undefined;

	function resetState() {
		setDevices([]);
		setFirmwareFileName(null);
		setOffset(0);
		setHasMore(false);
	}

	const _tableType = TableStyles_Types.primaryTable

	const columns = useColumnConstructor({
		accessorData: columnsData,
		tableStyle: _tableType,
		canEdit: false,
	});

	const tableData = useMemo(() =>
		devices.map(device => ({
			...device,
			shouldUpdateJSX: (
				<CheckBox
					checkStyle={CheckStyles.greyTertiary}
					isChecked={device.shouldUpdate}
					onChange={(checked) => {
						setDevices(prev => {
							const updated = [...prev];
							const idx = updated.findIndex(d => d.id === device.id);
							updated[idx] = { ...updated[idx], shouldUpdate: checked };
							return updated;
						});
					}}
					disabled={loadingUpdate}
				/>
			),
			resultJSX: device.success === undefined ? undefined : (
				device.success ? <glyphs.Check fillColor={colors.systemGreen} /> : <glyphs.Error fillColor={colors.systemRed} />
			)
		})),
		[devices, loadingUpdate]
	);

	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
	});

	return (
		<frames.Page_inner>
				<PageHeader topHeader='Software' subHeader='IO Firmware' />
			<frames._page_inner_content style={{display: "flex", flexDirection: "column", gap: "20px"}}>

				<AreaLayout
					tableType={TableTypes.fitContent}
					headerArea={mobileXs ? undefined : <AicoConnection hideLANControllers inTable/>}
					buttonArea={<div style={{ display: "flex", gap: "5px" }}>
						{mobileXs && <AicoConnection hideLANControllers inTable/>}
						<NewButton
							content={loadingAvailable ? "Fetching..." : "Fetch avail."}
							buttonType={ButtonTypes.tableButton}
							startEnhancer={glyphs.Fetch({})}
							onClick={() => {
								if (connectionState.domain) {
									fetchAvailable({ variables: { domain: connectionState.domain } });
								}
							}}
						/>
					</div>
					}	
					contentArea={
						<div style={{ display: "flex", flexDirection: "column", gap: "50px", height: "100%", alignItems: "center" }}>
							<div style={{ display: "flex", gap: "10px", alignItems: "flex-end", flexWrap: "wrap", justifyContent: "center" }}>
								<NewSelect
									header="Update Firmware"
									placeholder="New firmware version"
									onChange={(selectedOption) => {
										if (selectedOption?.value) {
											setFirmwareFileName(selectedOption.value);
										}
									}}
									options={dataAvailable?.device.availableFirmware.map((firmwareVersion) => ({ label: firmwareVersion.fileName, value: firmwareVersion.fileName })) ?? []}
									fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
									isRequired={true}
									isSearchable={true}
									valueOverrides={{defaultValue: selectFirmwareFile}}
								/>
								<NewButton
									onClick={() => {
										if (firmwareFileName && connectionState.domain) {
											setDevices(prev => prev.map(d => ({ ...d, success: undefined })));

											const firmwareUpdateVariables: FirmwareUpdateCANArgs = {
												domain: connectionState.domain,
												fileName: firmwareFileName,
												isDirect: isDirect,
												deviceRegisterIds: devices.filter(e => e.shouldUpdate).map(e => e.device_register_id)
											};
											updateFirmware({ variables: firmwareUpdateVariables });
										}
									}}
									disabled={loadingUpdate || !firmwareFileName || !devices.some(e => e.shouldUpdate)}
									content={"Update Firmware"}
									overrides={{
										baseStyles: { width: "fit-content", backgroundColor: colors.greyTertiary, height: "30px", padding: "0 10px" },
										contentStyles: { color: colors.greyDarkPrimary, textShadow: "none", ...fonts.fontSize4 },
										hoverStyles: { backgroundColor: colors.greyLightPrimary, color: colors.greyDarkPrimary }
									}}
								/>
								<div style={{height: "30px", display: "flex", alignItems: "center", marginLeft: "10px"}}>
									<CheckBox
										onChange={(isDirect) => {
											setIsDirect(isDirect);
										}}
										isChecked={isDirect}
										text="Direct"
										checkStyle={CheckStyles.transparent}
									/>
								</div>
							</div>

							<div style={{ width: "100%", maxWidth: "500px" }}>
								<div style={{ borderBottom: "2px solid " + colors.greyLightPrimary, textAlign: "left", ...fonts.fontSize3, ...fonts.header, color: colors.greyDarkPrimary }}>
									Available Firmware Versions
								</div>
								{loadingAvailable && <WaitAnimation />}
								{errorAvailable && <div style={{ ...css.error }}>Error</div>}
								{!loadingAvailable && !errorAvailable ? dataAvailable?.device.availableFirmware.length
									?
									dataAvailable.device.availableFirmware.map((firmwareVersion, i) => (
										<div key={i} style={{ display: "flex", flexDirection: "column", gap: "5px", textAlign: "left", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary }}>
											{firmwareVersion.fileName}
										</div>
									))
									:
									<div style={{ ...css.emptyPen, textAlign: "left", opacity: "0.5" }}>
										No firmware available
									</div>
									:
									<></>
								}
							</div>

							{loadingUpdate && <LoadingSpinner/>}
							{errorUpdate && <p style={{ ...css.error, maxWidth: "500px", textAlign: "center" }}>Error: Failed to update firmware</p>}

							{loadingDevices && <LoadingSpinner/>}
							{errorDevices && <p style={{ ...css.error, maxWidth: "500px", textAlign: "center" }}>Error: Failed to fetch devices</p>}
						
							
						</div>
					}
				/>

				{!loadingDevices && !errorDevices && devices.length > 0 &&
					<TableLayout
						fillScreenTable
						tableType={TableTypes.fitContent}
						buttonArea={
							<div style={{ display: "flex", gap: "5px" }}>
								<NewButton
									content={allSelected ? "Deselect all" : "Select all"}
									buttonType={ButtonTypes.tableButton}
									startEnhancer={glyphs.Check({})}
									onClick={() => {
										setDevices((prev) => {
											const newDevices = [];
											for (const device of prev) {
												newDevices.push({ ...device, shouldUpdate: allSelected ? false : true });
											}
											return newDevices;
										});
									}}
								/>
								{hasMore &&
									<NewButton
										content={"Fetch more"}
										buttonType={ButtonTypes.tableButton}
										startEnhancer={glyphs.Fetch({})}
										onClick={() => {
											if (connectionState.domain) {
												fetchDevices({ variables: { domain: connectionState.domain, limit: limit, offset: offset } })
											}
										}}
									/>
								}
							</div>
						}
						tableArea={
							<ReactiveTable
								table={table}
								tableType={_tableType}
								emptyText={t("noData", { defaultValue: "no data" })}
								renderFooter={false}
							/>
						}
					/>
				}
				
			</frames._page_inner_content>
		</frames.Page_inner>
	);
}